import { useEffect, useMemo, useState } from "react";
import { Download, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Copy, Check } from "lucide-react";

type SortDirection = "asc" | "desc" | null;

type ReportStatus = "unprocessed" | "processing" | "processed" | "error";

interface ApiReport {
  id: number;
  project_id: number;
  aiquery: string | null;
  started_at: string | null;  // ISO
  status: ReportStatus;
  paths: string[];            // JSON array in DB
  num_paths?: number | null;
  processed_paths?: number | null;
}

interface FileItem {
  name: string;
  url: string;
}

const statusText: Record<ReportStatus, string> = {
  unprocessed: "Queued. Waiting for worker to pick up.",
  processing: "Worker is processing documents.",
  processed:  "Report completed.",
  error:      "A prior attempt failed. Click the Continue Processing icon to continue from where it left off.",
};

function truncate(s: string, n = 50) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

  function getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return match ? match[2] : null;
  }

const Reports = () => {
  const [reports, setReports] = useState<ApiReport[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof ApiReport | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const [openDialogId, setOpenDialogId] = useState<number | null>(null);
  const [dialogFiles, setDialogFiles] = useState<FileItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  const [openAiDialogId, setOpenAiDialogId] = useState<number | null>(null);
  const [aiResponseLoading, setAiResponseLoading] = useState(false);
  const [aiResponseText, setAiResponseText] = useState("");
  const [aiResponseQuery, setAiResponseQuery] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetch("/api/reports", { headers: { Accept: "application/json" } });
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    })();
  }, []);

  async function openAiResponseDialog(reportId: number) {
    setOpenAiDialogId(reportId);
    setAiResponseLoading(true);
    setAiResponseText("");
    setAiResponseQuery("");
    try {
      const res = await fetch(`/api/reports/${reportId}`, { headers: { Accept: "application/json" } });
      const data = await res.json();
      setAiResponseText((data?.ai_response ?? "").toString());
      setAiResponseQuery((data?.aiquery ?? "").toString());
    } finally {
      setAiResponseLoading(false);
    }
  }

  async function handleCopyAiResponse() {
    const text = aiResponseText || "";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  }

  const sortedReports = useMemo(() => {
    if (!sortColumn || !sortDirection) return reports;
    const copy = [...reports];
    copy.sort((a, b) => {
      if (sortColumn === "started_at") {
        const at = a.started_at ? new Date(a.started_at).getTime() : 0;
        const bt = b.started_at ? new Date(b.started_at).getTime() : 0;
        return sortDirection === "asc" ? at - bt : bt - at;
      }
      const av = (a as any)[sortColumn] ?? "";
      const bv = (b as any)[sortColumn] ?? "";
      if (av < bv) return sortDirection === "asc" ? -1 : 1;
      if (av > bv) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [reports, sortColumn, sortDirection]);

  const handleSort = (column: keyof ApiReport) => {
    let direction: SortDirection = "asc";
    if (sortColumn === column && sortDirection === "asc") direction = "desc";
    else if (sortColumn === column && sortDirection === "desc") direction = null;
    setSortColumn(direction ? column : null);
    setSortDirection(direction);
  };

  const getSortIcon = (column: keyof ApiReport) => {
    if (sortColumn !== column) return null;
    if (sortDirection === "asc") return <ChevronUp className="h-4 w-4" />;
    if (sortDirection === "desc") return <ChevronDown className="h-4 w-4" />;
    return null;
    // :contentReference[oaicite:0]{index=0}
  };

  async function openFilesDialog(report: ApiReport) {
    setOpenDialogId(report.id);
    setLoadingFiles(true);
    try {
      const res = await fetch(`/api/reports/${report.id}/files`, { headers: { Accept: "application/json" } });
      const data = await res.json();
      setDialogFiles(Array.isArray(data.files) ? data.files : []);
    } finally {
      setLoadingFiles(false);
    }
  }

  async function handleExportAll(report: ApiReport) {
    try {
      setExporting(true);
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });
      const headers: HeadersInit = { Accept: "application/json", "Content-Type": "application/json" };
      const xsrfToken = getCookie("XSRF-TOKEN");
      if (xsrfToken) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);

      // If your export returns a file (zip), fetch as blob and download
      const res = await fetch(`/api/documents/${report.project_id}/export-query-files`, {
        method: "POST", 
        credentials: "include",
        headers,
        body: JSON.stringify({ paths: report.paths }), // or paths: report.paths
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${report.id}_files.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  async function handleReprocess(report: ApiReport) {
    setReprocessingId(report.id);
    try {
      await fetch("/sanctum/csrf-cookie", { credentials: "include" });

      // XSRF header is helpful with Sanctum; DO NOT set Content-Type manually
      const headers: HeadersInit = { Accept: "application/json" };
      const xsrf = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/)?.[1];
      if (xsrf) headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrf);

      await fetch(`/api/reports/${report.id}/reprocess`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({}),
      });
      // Optimistically update status to unprocessed so worker can pick it up
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: "processing" } : r));
    } finally {
      setReprocessingId(null);
    }
  }

  const getStatusBadge = (s: ReportStatus) => {
    const variants = {
      processed:  "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      processing: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
      unprocessed:"bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300",
      error:      "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    };
    return <span className={`px-2 py-1 rounded-full text-sm font-medium ${variants[s]}`}>
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground">View and manage your generated reports and associated documents.</p>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" className="h-auto p-0 font-medium hover:bg-transparent" onClick={() => handleSort("aiquery")}>
                  Report Name {getSortIcon("aiquery")}
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" className="h-auto p-0 font-medium hover:bg-transparent" onClick={() => handleSort("started_at")}>
                  Report Date {getSortIcon("started_at")}
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" className="h-auto p-0 font-medium hover:bg-transparent" onClick={() => handleSort("status")}>
                  Status {getSortIcon("status")}
                </Button>
              </TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedReports.map((r) => {
              const name = truncate(r.aiquery || "");
              const date = r.started_at ? new Date(r.started_at).toLocaleDateString() : "—";
              return (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                       <button
                        onClick={() => openAiResponseDialog(r.id)}
                        className="text-primary hover:underline text-left truncate max-w-[40ch]"
                        title="View full AI response"
                       >
                        {name}
                       </button>
                       {/* AI RESPONSE MODAL */}
                        <Dialog open={openAiDialogId === r.id} onOpenChange={(o) => { if (!o) setOpenAiDialogId(null); }}>
                            <DialogContent className="sm:max-w-[850px] w-[min(90vw,850px)] max-h-[85vh] overflow-y-auto overflow-x-hidden bg-white dark:bg-neutral-950">
                              <DialogHeader className="w-full max-w-full">
                                <DialogTitle className="max-w-full break-words [overflow-wrap:anywhere]">Full AI Response</DialogTitle>
                                <DialogDescription
                                  className="max-w-full break-words text-base [overflow-wrap:anywhere] whitespace-pre-wrap"
                                >
                                  Query: {aiResponseQuery}
                                </DialogDescription>
                              </DialogHeader>
                              <hr class="border-t border-gray-200 dark:border-gray-700" />
                              {/* Copy button row (between header and response), aligned far right */}
                              <div className="w-full flex justify-end">
                                <Button
                                  variant="ghost"
                                  onClick={handleCopyAiResponse}
                                  aria-label="Copy full AI response"
                                  title={copied ? "Text Copied!" : "Copy AI Response Text"}
                                  disabled={aiResponseLoading}
                                  className="gap-2"
                                >
                                  {copied ? (
                                    <>
                                      <Check className="h-4 w-4" />
                                      <span>Text Copied</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-4 w-4" />
                                      <span>Copy Text</span>
                                    </>
                                  )}
                                </Button>
                              </div>

                              {aiResponseLoading ? (
                                <div className="text-sm text-muted-foreground">Loading…</div>
                              ) : (
                                <div className="space-y-4 w-full max-w-full overflow-x-hidden">
                                  <div
                                    className="space-y-2 max-h-[60vh] overflow-y-auto overflow-x-hidden
                                               w-full max-w-full break-words [overflow-wrap:anywhere] whitespace-pre-wrap"
                                  >
                                    {aiResponseText || "No response captured yet."}
                                  </div>
                                </div>
                              )}
                            </DialogContent>
                        </Dialog>


                  </TableCell>
                  <TableCell>{date}</TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-help">{getStatusBadge(r.status)}</div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{statusText[r.status]}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* View/Download dialog */}
                      <Dialog open={openDialogId === r.id} onOpenChange={(o) => { if (!o) setOpenDialogId(null); }}>
                        <DialogTrigger asChild>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => openFilesDialog(r)}>
                                  <Download className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent><p>View Related Documents</p></TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </DialogTrigger>

                        {/* Max height, scroll, and no horizontal bleed */}
                        <DialogContent className="sm:max-w-[600px] w-[min(90vw,600px)] max-h-[85vh] overflow-y-auto overflow-x-hidden bg-white dark:bg-neutral-950">
                          <DialogHeader>
                            <DialogTitle>Related Documents</DialogTitle>
                            <DialogDescription>Documents associated with this report</DialogDescription>
                          </DialogHeader>

                          <div className="space-y-4">
                            {/* Make the list itself scrollable and prevent horizontal overflow */}
                            <div className="space-y-2 max-h-[60vh] overflow-y-auto overflow-x-hidden w-full max-w-full">
                              {loadingFiles ? (
                                <div className="text-sm text-muted-foreground">Loading…</div>
                              ) : dialogFiles.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No files.</div>
                              ) : dialogFiles.map((f, idx) => (
                                <div
                                    key={idx}
                                    // grid prevents the text cell from affecting the button column width
                                    className="grid grid-cols-[1fr_auto] items-center gap-2 p-2 border rounded bg-background w-full max-w-full overflow-hidden"
                                  >
                                    {/* text cell must be allowed to shrink: min-w-0 + overflow-hidden */}
                                    <div className="min-w-0 max-w-full overflow-hidden">
                                      {/* single-line ellipsis (no break-all) */}
                                      <span className="block text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                                        {f.name}
                                      </span>
                                    </div>

                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="shrink-0"
                                      onClick={() => window.open('/api/documents/download-by-path?path=' + f.url, "_blank")}
                                    >
                                      View
                                    </Button>
                                </div>
                              ))}
                            </div>
                            <Button
                                className="w-full inline-flex items-center justify-center gap-2"
                                onClick={() => handleExportAll(r)}
                                disabled={exporting}
                              >
                                {exporting ? (
                                  <>
                                    <span className="animate-spin h-4 w-4 rounded-full border-2 border-current border-t-transparent" />
                                    <span>Preparing…</span>
                                  </>
                                ) : (
                                  "Export All"
                                )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>


                      {/* Continue only if errored */}
                      {r.status === "error" && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => handleReprocess(r)} disabled={reprocessingId === r.id}>
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent><p>Continue Processing</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Reports;
